"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dispatchEventFactory = exports.consumeSubjectFactory = exports.tpSlProviderFactory = void 0;
const rxjs_1 = require("rxjs");
const Trade_1 = require("../../Trade");
const alwaysTrue = () => true;
/**
 * 工厂方法：该方法生成管理订单绑定的止盈止损的实例
 * @description
 * 该实例内部会缓存当前所有的活跃订单（限价单）绑定的止盈止损，每个订单最多绑定一对止盈止损
 *
 * 当获取到限价单列表的数据之后，会触发update函数调用，该函数接受订单的id列表，
 * 并基于订单id的列表发起网络请求，获取所有订单关联的止盈止损并存储
 *
 * 当某个订单被撤销或者被关闭（关闭一般是指被成交），会触发remove函数调用，该函数将移除对应的止盈止损数据
 *
 * 当止盈止损的数据发生变动之后会触发notifyDataChanged函数调用，触发UI层的数据更新
 *
 * @param params
 * - query 查询止盈止损的函数
 * - notifyDataChanged 通知数据更新的函数
 * - dataKey 基于传入的key来判断数据是否合法
 * - willInsertItem 可以实现该函数来控制是否要将数据更新到缓存中
 */
function tpSlProviderFactory(params) {
    const { query, notifyDataChanged, dataKey, willInsertItem = alwaysTrue } = params;
    // 用来缓存已经获取过数据的订单id
    const cachedIdSet = new Set();
    // 缓存订单关联的止盈
    const tpOrderMap = new Map();
    // 缓存订单关联的止损
    const slOrderMap = new Map();
    function cleanAllData() {
        // clean
        tpOrderMap.clear();
        slOrderMap.clear();
    }
    // 过滤掉已经被缓存的id
    function filterCachedIds(sourceIds) {
        return sourceIds.filter((id) => !cachedIdSet.has(id));
    }
    // 将id新增到缓存中
    function addIdsToCache(ids) {
        ids.forEach((id) => cachedIdSet.add(id));
    }
    /**将满足条件的条件单新增到缓存 */
    function insertNewOrder(it) {
        const key = it[dataKey] || "";
        // 由于后端数据问题，必须判断key的长度大于1才能证明这个值存在
        if (key.length > 1 && willInsertItem(it)) {
            const mode = Trade_1.TPSLUtils.getOrderTPSLMode(it);
            const map = mode === "tp" ? tpOrderMap : slOrderMap;
            map.set(key, it);
        }
    }
    function queryTriggers(ids) {
        if (ids.length) {
            query(ids).subscribe({
                next: (orders) => {
                    orders.forEach((it) => insertNewOrder(it));
                    // 注意：现在不支持在新建订单之后再绑定止盈止损，所以目前直接将所有id加到缓存里面，这样做是ok的
                    // FIXME: 当支持编辑订单绑定止盈止损时，这块逻辑需要修改
                    addIdsToCache(ids);
                    notifyDataChanged();
                },
                error: () => { }
            });
            return;
        }
    }
    /**
     * 当拉取限价单列表、或者新增订单止盈止损之后，将触发该函数的调用
     *
     * - 当 orders参数非空时，将直接将传入的条件单更新到缓存中
     *
     * - 否则判断传入的ids是否被缓存过，如果id未被缓存过，则发起网络请求获取id关联的止盈止损数据，并进行缓存
     * @param ids 订单id列表
     * @param orders 条件单列表
     */
    function update(ids, orders) {
        if (orders && orders.length) {
            orders.forEach(insertNewOrder);
            addIdsToCache(ids);
            notifyDataChanged();
            return;
        }
        const noCachedIds = filterCachedIds(ids);
        if (!noCachedIds.length) {
            return;
        }
        // 查询未被缓存的id的止盈止损单
        queryTriggers(noCachedIds);
    }
    /**
     * 移除ids关联的止盈止损单，或者直接移除传入的orders条件单列表
     * @param ids 订单id列表
     * @param orders 条件单列表
     */
    function remove(ids, orders) {
        if (ids.length) {
            ids.forEach((id) => {
                tpOrderMap.delete(id);
                slOrderMap.delete(id);
            });
        }
        else {
            if (orders && orders.length) {
                orders.forEach((it) => {
                    // 由于后端数据问题，必须判断key的长度大于1才能证明这个值存在
                    const key = it[dataKey] || "";
                    if (key.length > 1) {
                        const tp = tpOrderMap.get(key);
                        if ((tp === null || tp === void 0 ? void 0 : tp.trigger_id) === it.trigger_id) {
                            tpOrderMap.delete(key);
                        }
                        const sl = slOrderMap.get(key);
                        if ((sl === null || sl === void 0 ? void 0 : sl.trigger_id) === it.trigger_id) {
                            slOrderMap.delete(key);
                        }
                    }
                });
            }
        }
        notifyDataChanged();
    }
    return {
        update,
        tpOrderMap,
        slOrderMap,
        remove,
        cleanAllData,
    };
}
exports.tpSlProviderFactory = tpSlProviderFactory;
function consumeSubjectFactory() {
    /**用来从store中获取最新数据的，并且下发给其他订阅者 */
    const consumeSubject = new rxjs_1.BehaviorSubject(0);
    function notifyDataChanged() {
        // 跳出react的render阶段
        setTimeout(() => {
            consumeSubject.next(Date.now());
        }, 0);
    }
    return {
        notifyDataChanged,
        consumeSubject,
    };
}
exports.consumeSubjectFactory = consumeSubjectFactory;
function dispatchEventFactory(store) {
    const eventSubject = new rxjs_1.Subject();
    function dispatchEvent(event) {
        // 跳出react的render阶段
        setTimeout(() => {
            eventSubject.next(event);
        }, 0);
    }
    eventSubject.subscribe({
        next: (e) => {
            if (!e) {
                return;
            }
            const { event, ids, orders } = e;
            switch (event) {
                case "update": {
                    store.update(ids, orders);
                    break;
                }
                case "remove": {
                    store.remove(ids, orders);
                    break;
                }
                default: {
                    break;
                }
            }
        },
        error: () => { }
    });
    return dispatchEvent;
}
exports.dispatchEventFactory = dispatchEventFactory;
